import { useEffect, useRef, useState } from 'react';
import type { DocumentFieldView, PublicSignerView } from '@otter/shared';
import { api, ApiError } from '../lib/api';
import { loadPdf, renderPageToCanvas, type LoadedPdf } from '../lib/pdfRender';

// Standalone public signing page, reached only via a magic-link token at
// /sign/:token. Rendered outside the authenticated app shell (see main.tsx).

type Props = { token: string };

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; signer: PublicSignerView }
  | { kind: 'done' };

const PDF_WIDTH = 560;

function todayDmy(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

// Renders the typed name in a handwriting font onto a transparent canvas and
// returns a PNG data URL, used as the visible signature stamped into the PDF.
async function rasterizeSignature(name: string): Promise<string> {
  await document.fonts.load('600 64px "Dancing Script"');
  const scale = 3;
  const padX = 14;
  const height = 96;
  const maxWidth = 520;
  const measure = document.createElement('canvas').getContext('2d');
  if (!measure) throw new Error('Canvas unavailable');
  let fontSize = 64;
  measure.font = `600 ${fontSize}px "Dancing Script"`;
  while (measure.measureText(name).width > maxWidth && fontSize > 22) {
    fontSize -= 2;
    measure.font = `600 ${fontSize}px "Dancing Script"`;
  }
  const textW = Math.ceil(measure.measureText(name).width);
  const width = textW + padX * 2;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.scale(scale, scale);
  ctx.fillStyle = '#141414';
  ctx.textBaseline = 'middle';
  ctx.font = `600 ${fontSize}px "Dancing Script"`;
  ctx.fillText(name, padX, height / 2);
  return canvas.toDataURL('image/png');
}

export function SignPage({ token }: Props) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [consent, setConsent] = useState(false);
  const [sigName, setSigName] = useState('');
  const [fields, setFields] = useState<DocumentFieldView[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpError, setOtpError] = useState('');

  async function requestOtp() {
    setOtpBusy(true);
    setOtpError('');
    try {
      await api(`/v1/sign/${token}/otp/request`, { method: 'POST' });
      setOtpSent(true);
    } catch (e) {
      setOtpError(e instanceof ApiError ? e.message : 'No se pudo enviar el código');
    } finally {
      setOtpBusy(false);
    }
  }

  async function verifyOtp() {
    setOtpBusy(true);
    setOtpError('');
    try {
      await api(`/v1/sign/${token}/otp/verify`, { method: 'POST', body: { code: otpCode } });
      setOtpVerified(true);
    } catch (e) {
      setOtpError(e instanceof ApiError ? e.message : 'Código incorrecto');
    } finally {
      setOtpBusy(false);
    }
  }

  async function load() {
    try {
      const res = await api<{ signer: PublicSignerView; fields: DocumentFieldView[] }>(
        `/v1/sign/${token}`,
      );
      setState({ kind: 'ready', signer: res.signer });
      setSigName(res.signer.signerFullName);
      setFields(res.fields ?? []);
    } catch (e) {
      const message = e instanceof ApiError ? e.message : 'No se pudo cargar el documento';
      setState({ kind: 'error', message });
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const requiredTextOk = fields
    .filter((f) => f.type === 'text' && f.required)
    .every((f) => (values[f.id] ?? '').trim().length > 0);

  async function sign() {
    const name = sigName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const signatureImage = await rasterizeSignature(name);
      const fieldValues = fields
        .filter((f) => f.type === 'text')
        .map((f) => ({ fieldId: f.id, value: (values[f.id] ?? '').slice(0, 2000) }));
      await api(`/v1/sign/${token}/sign`, {
        method: 'POST',
        body: { consent: true, signatureName: name, signatureImage, fieldValues },
      });
      setState({ kind: 'done' });
    } catch (e) {
      const message = e instanceof ApiError ? e.message : 'No se pudo completar la firma';
      setState({ kind: 'error', message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sign-page">
      <div className="sign-card">
        <div className="sign-brand">Norlia · Firma electrónica</div>

        {state.kind === 'loading' && <p>Cargando…</p>}

        {state.kind === 'error' && (
          <>
            <h2>No disponible</h2>
            <p className="sign-muted">{state.message}</p>
          </>
        )}

        {state.kind === 'done' && (
          <>
            <h2>Documento firmado</h2>
            <p className="sign-muted">
              Gracias. Tu firma ha quedado registrada. Puedes cerrar esta ventana.
            </p>
          </>
        )}

        {state.kind === 'ready' && state.signer.alreadySigned && (
          <>
            <h2>Ya firmado</h2>
            <p className="sign-muted">Este documento ya fue firmado con este enlace.</p>
          </>
        )}

        {state.kind === 'ready' && state.signer.expired && (
          <>
            <h2>Enlace caducado</h2>
            <p className="sign-muted">El plazo para firmar este documento ha expirado.</p>
          </>
        )}

        {state.kind === 'ready' && !state.signer.alreadySigned && !state.signer.expired && (
          <>
            <h2>{state.signer.documentFilename}</h2>
            <p className="sign-muted">
              Hola {state.signer.signerFullName}. Revisa el documento y firma para continuar.
            </p>

            {fields.length > 0 ? (
              <SignerPdf
                token={token}
                fields={fields}
                sigName={sigName.trim()}
                values={values}
                onChange={(id, v) => setValues((s) => ({ ...s, [id]: v }))}
              />
            ) : (
              <iframe title="Documento a firmar" className="sign-pdf" src={`/v1/sign/${token}/pdf`} />
            )}

            <label className="sign-label">Tu firma</label>
            <input
              className="sign-input"
              value={sigName}
              onChange={(e) => setSigName(e.target.value)}
              placeholder="Escribe tu nombre completo"
              maxLength={120}
            />
            <div className="sign-preview">{sigName.trim() || 'Tu firma'}</div>

            {state.signer.otpRequired && (
              <div className="sign-otp">
                <label className="sign-label">Verificación por SMS</label>
                {otpVerified ? (
                  <div className="sign-otp-ok">✓ Teléfono verificado</div>
                ) : !otpSent ? (
                  <button className="btn" disabled={otpBusy} onClick={() => void requestOtp()}>
                    {otpBusy ? 'Enviando…' : 'Enviar código por SMS'}
                  </button>
                ) : (
                  <div className="sign-otp-row">
                    <input
                      className="sign-input"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="Código de 6 dígitos"
                    />
                    <button
                      className="btn primary"
                      disabled={otpBusy || otpCode.length !== 6}
                      onClick={() => void verifyOtp()}
                    >
                      Verificar
                    </button>
                    <button className="btn ghost" disabled={otpBusy} onClick={() => void requestOtp()}>
                      Reenviar
                    </button>
                  </div>
                )}
                {otpError && <div className="sign-otp-err">{otpError}</div>}
              </div>
            )}

            <label className="sign-consent">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>
                He leído el documento y acepto firmarlo electrónicamente con la firma de arriba.
                Entiendo que tiene validez legal.
              </span>
            </label>

            <button
              className="btn primary"
              disabled={
                !consent ||
                !sigName.trim() ||
                !requiredTextOk ||
                (state.signer.otpRequired && !otpVerified) ||
                busy
              }
              onClick={() => void sign()}
            >
              {busy ? 'Firmando…' : 'Firmar documento'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Renders the PDF with pdf.js and overlays this signer's fields on top, so they
// can be filled in place. Signature fields preview the typed signature; date
// fields show today's date (the server stamps the actual signing date).
function SignerPdf(props: {
  token: string;
  fields: DocumentFieldView[];
  sigName: string;
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
}) {
  const [pdf, setPdf] = useState<LoadedPdf | null>(null);
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const doc = await loadPdf(`/v1/sign/${props.token}/pdf`);
        if (cancelled) return;
        setPdf(doc);
        setNumPages(doc.numPages);
      } catch {
        // Render falls back to nothing; the signer can still sign.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.token]);

  if (!pdf) return <div className="sign-muted" style={{ margin: '16px 0' }}>Cargando documento…</div>;

  return (
    <div className="sign-doc">
      {Array.from({ length: numPages }, (_, i) => (
        <SignerPage
          key={i}
          pdf={pdf}
          pageIndex={i}
          fields={props.fields.filter((f) => f.page === i)}
          sigName={props.sigName}
          values={props.values}
          onChange={props.onChange}
        />
      ))}
    </div>
  );
}

function SignerPage(props: {
  pdf: LoadedPdf;
  pageIndex: number;
  fields: DocumentFieldView[];
  sigName: string;
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!canvasRef.current) return;
      const s = await renderPageToCanvas(props.pdf, props.pageIndex + 1, canvasRef.current, PDF_WIDTH);
      if (!cancelled) setSize({ w: s.width, h: s.height });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.pdf, props.pageIndex]);

  return (
    <div
      className="sign-page-wrap"
      style={{ width: PDF_WIDTH, height: size?.h ?? PDF_WIDTH * 1.4 }}
    >
      <canvas ref={canvasRef} className="sign-canvas" />
      {props.fields.map((f) => {
        const style = {
          left: `${f.x * 100}%`,
          top: `${f.y * 100}%`,
          width: `${f.w * 100}%`,
          height: `${f.h * 100}%`,
        } as const;
        if (f.type === 'text') {
          return (
            <input
              key={f.id}
              className="sign-field-input"
              style={style}
              value={props.values[f.id] ?? ''}
              placeholder="Escribe aquí"
              onChange={(e) => props.onChange(f.id, e.target.value)}
            />
          );
        }
        if (f.type === 'date') {
          return (
            <div key={f.id} className="sign-field-static" style={style}>
              {todayDmy()}
            </div>
          );
        }
        return (
          <div key={f.id} className="sign-field-sig" style={style}>
            {props.sigName || 'Firma aquí'}
          </div>
        );
      })}
    </div>
  );
}
